import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Container, Typography, Box, Card, CardContent, TextField, Button,
  FormControl, InputLabel, Select, MenuItem, CircularProgress,
  Chip, alpha, Checkbox, FormControlLabel, FormGroup, IconButton, InputAdornment,
} from '@mui/material';
import {
  Save as SaveIcon, Event as EventIcon, EventAvailable as EventSidebarIcon, Add as AddIcon, Delete as DeleteIcon,
  Group as GroupIcon, Person as PersonIcon, Engineering as EngineeringIcon,
  Computer as ComputerIcon, Settings as SettingsIcon, Schedule as ScheduleIcon,
  LocationOn as LocationIcon, Notes as NotesIcon, Flag as FlagIcon,
  DriveFileRenameOutline as NameIcon, CalendarMonth as CalendarMonthIcon, CalendarToday as CalendarTodayIcon,
} from '@mui/icons-material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { eventService } from '../../../services/eventService';
import { useAuth } from '../../../contexts/AuthContext';
import dayjs from 'dayjs';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import FeedbackMessage from '../../common/FeedbackMessage';
import ConfirmDeleteDialog from '../../common/ConfirmDeleteDialog';
import axiosInstance from '../../../utils/axios';
import { motion } from 'framer-motion';
import SubmitActionsCard from '../../common/SubmitActionsCard';
import FormDraftNotice from '../../common/FormDraftNotice';
import useFormDraftBindings from '../../../hooks/useFormDraftBindings';
import CommonSectionCard from '../../common/SectionCard';
import SearchableSelect from '../../common/SearchableSelect';
import AudiencePickerColumn from './AudiencePickerColumn';
import { inputRootSx, makePickerSlots, buildPickerFieldProps, FORM_NEON, FORM_TEXT_BODY, FORM_SURFACE_UP } from '../../common/formFieldSx';
import {
  applyApiValidationErrors,
  createFieldErrorGate,
  formatApiErrorMessage,
  formatFieldErrorsMessage,
} from '../../../utils/formValidationFeedback';
import { toPickerTime, formatPickerTime } from '../../../utils/malaysiaTime';

// ─── Design tokens ─────────────────────────────────────────────────────────────
const B = {
  600: '#2563EB', 700: '#1D4ED8', 800: '#1E40AF', 900: '#1E3A8A',
};
const NEON  = '#38BDF8';
const NEON2 = '#818CF8';
const NEON3 = '#34D399';

const SURFACE_BASE = '#050d1a';
const SURFACE_MID  = '#0a1628';
const SURFACE_UP   = '#0f1f38';

// ─── Global CSS ────────────────────────────────────────────────────────────────
// Key fix: the picker TextField uses MuiPickersTextField / MuiPickersOutlinedInput
// which has its OWN set of class names that bypass the normal inputRootSx.
// We override all of them here to match the StyledField appearance exactly:
//   • same border-radius (10px)
//   • same border colour tokens
//   • same focus glow
//   • same label colour / weight
//   • NO notch cut (we can't remove the notch from CSS alone, but we make it
//     visually invisible by matching the outline approach used in inputRootSx)
const FORM_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600&family=Rajdhani:wght@500;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
  @keyframes form-pulse   { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }
  @keyframes form-glow-in { from { box-shadow: 0 0 0 0 rgba(56,189,248,0.45); } to { box-shadow: 0 0 0 4px rgba(56,189,248,0); } }
  @keyframes neon-ping    { 0% { transform: scale(0.8); opacity: 0.9; } 100% { transform: scale(2.2); opacity: 0; } }

  /* ── Picker popup theming ────────────────────────────────────────────────── */
  .MuiPickersPopper-root .MuiPaper-root,
  .MuiDateCalendar-root,
  .MuiPickersLayout-root {
    background: #0a1628 !important;
    color: #E2E8F0 !important;
    border: 1px solid rgba(56,189,248,0.22) !important;
    border-radius: 12px !important;
    box-shadow: 0 8px 32px rgba(0,0,0,0.65) !important;
  }
  .MuiDayCalendar-weekDayLabel {
    color: rgba(56,189,248,0.65) !important;
    font-family: 'JetBrains Mono', monospace !important;
    font-size: 0.65rem !important;
    font-weight: 600 !important;
    letter-spacing: 1px !important;
  }
  .MuiPickersDay-root {
    color: #CBD5E1 !important;
    font-family: 'DM Sans', sans-serif !important;
    font-size: 0.85rem !important;
    background: transparent !important;
    border-radius: 8px !important;
    transition: all 0.2s ease !important;
  }
  .MuiPickersDay-root:hover {
    background: rgba(56,189,248,0.18) !important;
    color: #38BDF8 !important;
    box-shadow: 0 0 8px rgba(56,189,248,0.3) !important;
  }
  .MuiPickersDay-root.Mui-selected {
    background: linear-gradient(135deg,#38BDF8 60%,#0EA5E9 100%) !important;
    color: #050d1a !important;
    border: 2px solid #38BDF8 !important;
    font-weight: 700 !important;
    box-shadow: 0 0 12px rgba(56,189,248,0.6) !important;
  }
  .MuiPickersDay-root.MuiPickersDay-today:not(.Mui-selected) {
    border: 2px solid rgba(56,189,248,0.65) !important;
    color: #38BDF8 !important;
    background: rgba(56,189,248,0.08) !important;
  }
  .MuiPickersCalendarHeader-label {
    color: #E2E8F0 !important;
    font-family: 'Rajdhani', sans-serif !important;
    font-weight: 700 !important;
    font-size: 1rem !important;
  }
  .MuiPickersArrowSwitcher-button,
  .MuiPickersCalendarHeader-switchViewButton {
    color: rgba(56,189,248,0.75) !important;
  }
  .MuiPickersArrowSwitcher-button:hover,
  .MuiPickersCalendarHeader-switchViewButton:hover {
    background: rgba(56,189,248,0.1) !important;
  }
  .MuiYearCalendar-root button,
  .MuiMonthCalendar-root button {
    color: #CBD5E1 !important;
    font-family: 'DM Sans', sans-serif !important;
  }
  .MuiYearCalendar-root button:hover,
  .MuiMonthCalendar-root button:hover {
    background: rgba(56,189,248,0.1) !important;
    color: #38BDF8 !important;
  }
  .MuiYearCalendar-root button.Mui-selected,
  .MuiMonthCalendar-root button.Mui-selected {
    background: linear-gradient(135deg,#38BDF8 60%,#0EA5E9 100%) !important;
    color: #050d1a !important;
    font-weight: 700 !important;
    box-shadow: 0 0 12px rgba(56,189,248,0.5) !important;
    border-radius: 8px !important;
  }
  .MuiClock-root { background: #0a1628 !important; }
  .MuiClock-pin  { background: #38BDF8 !important; }
  .MuiClockPointer-root  { background: rgba(56,189,248,0.75) !important; }
  .MuiClockPointer-thumb { border-color: #38BDF8 !important; background: #38BDF8 !important; }
  .MuiClockNumber-root   { color: #CBD5E1 !important; font-family: 'DM Sans', sans-serif !important; }
  .MuiPickersToolbar-root {
    background: #0f1f38 !important;
    border-bottom: 1px solid rgba(56,189,248,0.12) !important;
  }
  .MuiPickersToolbar-title,
  .MuiDateTimePickerToolbar-separator { color: #38BDF8 !important; }
  .MuiDialogActions-root .MuiButton-root {
    color: #38BDF8 !important;
    font-family: 'DM Sans', sans-serif !important;
    font-weight: 600 !important;
  }
  .MuiMultiSectionDigitalClock-root { background: #0a1628 !important; }
  .MuiMultiSectionDigitalClockSection-item {
    color: #94A3B8 !important;
    font-family: 'DM Sans', sans-serif !important;
  }
  .MuiMultiSectionDigitalClockSection-item.Mui-selected {
    color: #050d1a !important;
    background: linear-gradient(135deg,#38BDF8 60%,#0EA5E9 100%) !important;
    font-weight: 700 !important;
    box-shadow: 0 0 8px rgba(56,189,248,0.5) !important;
    border-radius: 4px !important;
  }
  .MuiMultiSectionDigitalClockSection-root:not(:first-child) {
    border-left: 1px solid rgba(56,189,248,0.1) !important;
  }

`;

const TEXT_BODY  = FORM_TEXT_BODY;
const TEXT_MUTED = '#94A3B8';

const darkModeMenuSx = {
  '& .MuiPaper-root': {
    background: `linear-gradient(160deg, ${alpha(SURFACE_BASE, 0.98)} 0%, ${alpha(SURFACE_MID, 0.96)} 100%)`,
    border: `1px solid ${alpha(NEON, 0.2)}`,
    boxShadow: `0 8px 32px ${alpha('#000', 0.6)}, inset 0 1px 0 ${alpha(NEON, 0.1)}`,
    backdropFilter: 'blur(12px)',
  },
  '& .MuiMenuItem-root': {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '0.9rem',
    color: alpha(TEXT_BODY, 0.85),
    '&:hover': { background: `linear-gradient(135deg, ${alpha(NEON, 0.12)}, ${alpha(B[700], 0.08)})`, color: NEON },
    '&.Mui-selected': {
      background: `linear-gradient(135deg, ${alpha(NEON, 0.18)}, ${alpha(B[700], 0.12)})`,
      color: NEON,
      fontWeight: 600,
      '&:hover': { background: `linear-gradient(135deg, ${alpha(NEON, 0.22)}, ${alpha(B[700], 0.15)})` },
    },
  },
};

const StyledField = ({ sx, ...props }) => (
  <TextField {...props} sx={{ ...inputRootSx, ...sx }} />
);

const StyledSelect = ({ label, value, onChange, children, required, startIcon, sx }) => (
  <FormControl fullWidth required={required} sx={{ flex: 1, ...inputRootSx, ...sx }}>
    <InputLabel sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.875rem', color: alpha(NEON, 0.45), '&.Mui-focused': { color: alpha(NEON, 0.85), fontWeight: 600 } }}>
      {label}
    </InputLabel>
    <Select
      value={value}
      onChange={onChange}
      label={label}
      startAdornment={startIcon ? <InputAdornment position="start">{startIcon}</InputAdornment> : undefined}
      MenuProps={{
        PaperProps: {
          sx: {
            background: `linear-gradient(160deg, ${alpha(SURFACE_BASE, 0.98)} 0%, ${alpha(SURFACE_MID, 0.96)} 100%)`,
            border: `1px solid ${alpha(NEON, 0.2)}`,
            boxShadow: `0 8px 32px ${alpha('#000', 0.6)}, inset 0 1px 0 ${alpha(NEON, 0.1)}`,
            backdropFilter: 'blur(12px)',
            '& .MuiMenuItem-root': {
              fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', color: alpha(TEXT_BODY, 0.85),
              transition: 'all 0.15s ease',
              '&:hover': { background: `linear-gradient(135deg, ${alpha(NEON, 0.12)}, ${alpha(B[700], 0.08)})`, color: NEON },
              '&.Mui-selected': {
                background: `linear-gradient(135deg, ${alpha(NEON, 0.18)}, ${alpha(B[700], 0.12)})`, color: NEON, fontWeight: 600,
                '&:hover': { background: `linear-gradient(135deg, ${alpha(NEON, 0.22)}, ${alpha(B[700], 0.15)})` },
              },
            },
          },
        },
      }}
      sx={{
        borderRadius: '10px',
        fontFamily: "'DM Sans', sans-serif",
        color: alpha(TEXT_BODY, 0.85),
        background: alpha(SURFACE_UP, 0.7),
        backdropFilter: 'blur(8px)',
        '& .MuiOutlinedInput-notchedOutline': { borderColor: alpha(NEON, 0.18) },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: alpha(NEON, 0.4) },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: alpha(NEON, 0.65), borderWidth: '1.5px' },
        '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(NEON, 0.12)}, 0 0 14px ${alpha(NEON, 0.1)}` },
        '& .MuiSelect-select': { color: `${alpha(TEXT_BODY, 0.85)} !important`, WebkitTextFillColor: `${alpha(TEXT_BODY, 0.85)} !important` },
        '& .MuiSelect-icon': { color: alpha(NEON, 0.45) },
      }}
    >
      {children}
    </Select>
  </FormControl>
);

const PeriodBox = ({ label, neon, children }) => (
  <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '240px', p: 2, borderRadius: '12px', background: alpha(neon, 0.06), border: `1px solid ${alpha(neon, 0.22)}` }}>
    <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.68rem', fontWeight: 600, letterSpacing: 0.8, mb: 1.5, color: neon }}>
      {label}
    </Typography>
    {children}
  </Box>
);

const SectionCard = ({ title, icon, delay = 0, children, accentNeon }) => (
  <CommonSectionCard title={title} icon={icon} delay={delay} accentNeon={accentNeon}>
    {children}
  </CommonSectionCard>
);

function teamDisplayLabel(team, index) {
  const num = index + 1;
  const name = team.team_name?.trim();
  return name ? `Team ${num} (${name})` : `Team ${num}`;
}

const TRAINING_MEMBER_FIELDS = [
  { field: 'operator_1_id', label: 'Operator 1' },
  { field: 'operator_2_id', label: 'Operator 2' },
  { field: 'operator_3_id', label: 'Operator 3' },
  { field: 'pit_crew_1_id', label: 'Pit Crew 1' },
  { field: 'pit_crew_2_id', label: 'Pit Crew 2' },
  { field: 'pit_crew_3_id', label: 'Pit Crew 3' },
];

const TRAINING_ROLE_GROUPS = [
  {
    groupLabel: 'Operators',
    accent: NEON3,
    fields: TRAINING_MEMBER_FIELDS.filter((f) => f.field.startsWith('operator_')),
  },
  {
    groupLabel: 'Pit Crew',
    accent: NEON2,
    fields: TRAINING_MEMBER_FIELDS.filter((f) => f.field.startsWith('pit_crew_')),
  },
];

const DEMO_DEPT_FIELDS = [
  ['Programming', 'programming_members'],
  ['Electronic', 'electronic_members'],
  ['Mechanism', 'mechanism_members'],
];

const LAB_REP_FIELDS = [
  ['Programming Room', 'programming_representatives'],
  ['Electronic Room', 'electronic_representatives'],
  ['Mechanism Room', 'mechanism_representatives'],
  ['Inventory', 'inventory_representatives'],
  ['Meeting', 'meeting_representatives'],
  ['Mop Floor', 'mop_floor_representatives'],
];

function teamDeptFieldKey(teamIndex, field) {
  return `team_${teamIndex}_${field}`;
}

function collectDemoPrototypeTeamFieldErrors(formData) {
  const errors = {};
  if (!formData.teams?.length) {
    errors.teams = 'At least one team is required';
  }
  (formData.teams || []).forEach((team, index) => {
    if (!team.team_name?.trim()) {
      errors[`team_${index}_team_name`] = 'Team name is required';
    }
    DEMO_DEPT_FIELDS.forEach(([label, field]) => {
      if (!team[field]?.length) {
        errors[teamDeptFieldKey(index, field)] = `Select at least one ${label} member`;
      }
    });
  });
  return errors;
}

function collectLabCleaningFieldErrors(formData) {
  const errors = {};
  LAB_REP_FIELDS.forEach(([label, field]) => {
    if (!Array.isArray(formData[field]) || formData[field].length < 1) {
      errors[field] = `Select at least one representative for ${label}`;
    }
  });
  return errors;
}

const isTrainingMemberSelected = (id) => id !== '' && id != null;

function buildTrainingMemberOptions(team, field, allUsers) {
  const currentValue = team[field] === '' || team[field] == null ? null : Number(team[field]);
  const selectedByOtherRole = [
    team.operator_1_id,
    team.operator_2_id,
    team.operator_3_id,
    team.pit_crew_1_id,
    team.pit_crew_2_id,
    team.pit_crew_3_id,
  ]
    .filter((id) => id !== '' && id != null)
    .map((id) => Number(id))
    .filter((id) => id !== currentValue);

  return allUsers
    .filter((u) => !selectedByOtherRole.includes(Number(u.id)))
    .map((u) => ({ value: u.id, label: u.name }));
}

const isValidPickerDate = (v) => {
  if (v == null) return false;
  if (dayjs.isDayjs(v)) return v.isValid();
  return dayjs(v).isValid();
};

const isValidPickerTime = (v) => {
  if (v == null) return false;
  if (dayjs.isDayjs(v)) return v.isValid();
  return dayjs(v).isValid();
};

const PROTOTYPE_TIMELINE_PAIRS = [
  ['Design', 'design_start_date', 'design_end_date'],
  ['Mechanism', 'mechanism_start_date', 'mechanism_end_date'],
  ['Electronic', 'electronic_start_date', 'electronic_end_date'],
  ['Programming', 'programming_start_date', 'programming_end_date'],
  ['Tuning', 'tuning_start_date', 'tuning_end_date'],
];

/** Required-field presence only — used to enable Create/Update. */
function collectEventFormRequiredFieldIssues(formData, eventType) {
  const issues = [];

  if (!formData.name?.trim()) issues.push('event name');
  if (!formData.type) issues.push('event type');
  if (!isValidPickerDate(formData.start_date)) issues.push('start date');
  if (!isValidPickerDate(formData.end_date)) issues.push('end date');
  if (!formData.venue?.trim()) issues.push('venue');

  if (['Demo', 'Prototype'].includes(eventType)) {
    if (!formData.teams?.length) issues.push('at least one team');

    for (const [label, startKey, endKey] of PROTOTYPE_TIMELINE_PAIRS) {
      if (!isValidPickerDate(formData[startKey]) || !isValidPickerDate(formData[endKey])) {
        issues.push(`${label} phase start & end`);
      }
    }

    if (!isValidPickerDate(formData.demo_day) || !isValidPickerTime(formData.demo_time)) {
      issues.push('demo day date & time');
    }

    (formData.teams || []).forEach((team, i) => {
      const label = teamDisplayLabel(team, i);
      if (!team.team_name?.trim()) issues.push(`${label}: team name`);
      const missingRoles = [];
      if (!team.programming_members?.length) missingRoles.push('Programming');
      if (!team.electronic_members?.length) missingRoles.push('Electronic');
      if (!team.mechanism_members?.length) missingRoles.push('Mechanism');
      if (missingRoles.length === 1) {
        issues.push(`${label}: select at least one ${missingRoles[0]} member`);
      } else if (missingRoles.length > 1) {
        issues.push(`${label}: select at least one member for ${missingRoles.join(', ')}`);
      }
    });
  }

  if (eventType === 'Training') {
    if (!formData.teams?.length) issues.push('at least one team');
    (formData.teams || []).forEach((team, i) => {
      const label = teamDisplayLabel(team, i);
      if (!team.team_name?.trim()) issues.push(`${label}: team name`);
      const missingRoles = TRAINING_MEMBER_FIELDS
        .filter(({ field }) => !isTrainingMemberSelected(team[field]))
        .map(({ label: roleLabel }) => roleLabel);
      if (missingRoles.length === 1) {
        issues.push(`${label}: ${missingRoles[0]} is required`);
      } else if (missingRoles.length > 1) {
        issues.push(`${label}: assign ${missingRoles.join(', ')}`);
      }
    });
  }

  if (eventType === 'LabCleaning') {
    for (const [label, field] of LAB_REP_FIELDS) {
      if (!Array.isArray(formData[field]) || formData[field].length < 1) {
        issues.push(`${label}: select at least one representative`);
      }
    }
  }

  return issues;
}

/** Full submit-time rules including cross-field date/time checks. */
function collectEventFormBlockingIssues(formData, eventType) {
  const issues = collectEventFormRequiredFieldIssues(formData, eventType);

  const eventStart = dayjs(formData.start_date);
  const eventEnd = dayjs(formData.end_date);
  const eventWindowOk = eventStart.isValid() && eventEnd.isValid();

  if (eventWindowOk && eventEnd.isBefore(eventStart, 'day')) {
    issues.push('end date on/after start date');
  }

  if (isValidPickerTime(formData.start_time) && isValidPickerTime(formData.end_time)) {
    const startTime = dayjs(formData.start_time);
    const endTime = dayjs(formData.end_time);
    if (!endTime.isAfter(startTime)) {
      issues.push('end time after start time');
    }
  }

  if (['Demo', 'Prototype'].includes(eventType)) {
    for (const [label, startKey, endKey] of PROTOTYPE_TIMELINE_PAIRS) {
      const phaseStart = formData[startKey];
      const phaseEnd = formData[endKey];
      if (!isValidPickerDate(phaseStart) || !isValidPickerDate(phaseEnd)) continue;

      const phaseStartDay = dayjs(phaseStart);
      const phaseEndDay = dayjs(phaseEnd);
      if (phaseEndDay.isBefore(phaseStartDay, 'day')) {
        issues.push(`${label} phase: end on/after start`);
      }
      if (eventWindowOk && (phaseStartDay.isBefore(eventStart, 'day') || phaseEndDay.isAfter(eventEnd, 'day'))) {
        issues.push(`${label} phase inside event dates`);
      }
    }

    if (isValidPickerDate(formData.demo_day) && eventWindowOk) {
      const demoDay = dayjs(formData.demo_day);
      if (demoDay.isBefore(eventStart, 'day') || demoDay.isAfter(eventEnd, 'day')) {
        issues.push('demo day inside event dates');
      }
    }
  }

  if (eventType === 'Training') {
    (formData.teams || []).forEach((team, i) => {
      const label = teamDisplayLabel(team, i);
      const operatorIds = [team.operator_1_id, team.operator_2_id, team.operator_3_id]
        .filter(isTrainingMemberSelected)
        .map((id) => Number(id));
      const pitCrewIds = [team.pit_crew_1_id, team.pit_crew_2_id, team.pit_crew_3_id]
        .filter(isTrainingMemberSelected)
        .map((id) => Number(id));
      if (operatorIds.some((id) => pitCrewIds.includes(id))) {
        issues.push(`${label}: the same person cannot be both Operator and Pit Crew`);
      }
    });
  }

  return issues;
}

function collectEventFormFieldErrors(formData, eventType) {
  const errors = {};
  const isValidPickerDate = (v) => {
    if (v == null) return false;
    if (dayjs.isDayjs(v)) return v.isValid();
    return dayjs(v).isValid();
  };
  const isValidPickerTime = (v) => {
    if (v == null) return false;
    if (dayjs.isDayjs(v)) return v.isValid();
    return dayjs(v).isValid();
  };

  if (!formData.name?.trim()) errors.name = 'Event name is required';
  if (!formData.type && !eventType) errors.type = 'Event type is required';
  if (!isValidPickerDate(formData.start_date)) errors.start_date = 'Start date is required';
  if (!isValidPickerDate(formData.end_date)) errors.end_date = 'End date is required';
  if (!formData.venue?.trim()) errors.venue = 'Venue is required';

  const eventStart = dayjs(formData.start_date);
  const eventEnd = dayjs(formData.end_date);
  const eventWindowOk = eventStart.isValid() && eventEnd.isValid();

  if (eventWindowOk && eventEnd.isBefore(eventStart, 'day')) {
    errors.end_date = 'The end date field must be a date after or equal to start date.';
  }

  if (isValidPickerTime(formData.start_time) && isValidPickerTime(formData.end_time)) {
    const startTime = dayjs(formData.start_time);
    const endTime = dayjs(formData.end_time);
    if (!endTime.isAfter(startTime)) {
      errors.end_time = 'The end time field must be a date after start time.';
    }
  }

  const activeType = formData.type || eventType;
  if (['Demo', 'Prototype'].includes(activeType)) {
    const timelinePairs = [
      ['Design', 'design_start_date', 'design_end_date'],
      ['Mechanism', 'mechanism_start_date', 'mechanism_end_date'],
      ['Electronic', 'electronic_start_date', 'electronic_end_date'],
      ['Programming', 'programming_start_date', 'programming_end_date'],
      ['Tuning', 'tuning_start_date', 'tuning_end_date'],
    ];

    for (const [label, startKey, endKey] of timelinePairs) {
      const phaseStart = formData[startKey];
      const phaseEnd = formData[endKey];
      if (!isValidPickerDate(phaseStart)) errors[startKey] = `${label} start date is required`;
      if (!isValidPickerDate(phaseEnd)) errors[endKey] = `${label} end date is required`;
      if (!isValidPickerDate(phaseStart) || !isValidPickerDate(phaseEnd)) continue;

      const phaseStartDay = dayjs(phaseStart);
      const phaseEndDay = dayjs(phaseEnd);
      if (phaseEndDay.isBefore(phaseStartDay, 'day')) {
        errors[endKey] = `${label} end date must be on or after start date`;
      }
      if (eventWindowOk && phaseStartDay.isBefore(eventStart, 'day')) {
        errors[startKey] = `${label} phase must be within event dates`;
      }
      if (eventWindowOk && phaseEndDay.isAfter(eventEnd, 'day')) {
        errors[endKey] = `${label} phase must be within event dates`;
      }
    }

    if (!isValidPickerDate(formData.demo_day)) errors.demo_day = 'Demo day is required';
    if (!isValidPickerTime(formData.demo_time)) errors.demo_time = 'Demo time is required';
    else if (eventWindowOk && isValidPickerDate(formData.demo_day)) {
      const demoDay = dayjs(formData.demo_day);
      if (demoDay.isBefore(eventStart, 'day') || demoDay.isAfter(eventEnd, 'day')) {
        errors.demo_day = 'Demo day must be within event dates';
      }
    }

    Object.assign(errors, collectDemoPrototypeTeamFieldErrors(formData));
  }

  if (activeType === 'LabCleaning') {
    Object.assign(errors, collectLabCleaningFieldErrors(formData));
  }

  return errors;
}

function getEventFormValidationMessage(formData, eventType) {
  const blocking = collectEventFormBlockingIssues(formData, eventType);
  if (!blocking.length) return null;
  if (blocking.length === 1) return blocking[0];
  return `Please fix the following: ${blocking.join('. ')}.`;
}

// ─── Main Component ────────────────────────────────────────────────────────────
const EventForm = ({ isEdit = false }) => {
  const navigate = useNavigate();
  const { id }   = useParams();
  const { user: currentUser } = useAuth();
  const [searchParams] = useSearchParams();

  const [loading,                  setLoading]                  = useState(false);
  const [error,                    setError]                    = useState(null);
  const [success,                  setSuccess]                  = useState(null);
  const [eventType,                setEventType]                = useState('');
  const [teamDeleteDialogOpen,     setTeamDeleteDialogOpen]     = useState(false);
  const [teamDeleteIndex,          setTeamDeleteIndex]          = useState(null);
  const [confirmUpdateDialogOpen,  setConfirmUpdateDialogOpen]  = useState(false);
  const [confirmUpdateError,       setConfirmUpdateError]       = useState('');
  const [pendingUpdateRequest,     setPendingUpdateRequest]     = useState(null);
  const [users,                    setUsers]                    = useState({ programming: [], electronic: [], mechanism: [], all: [] });
  const [departments,              setDepartments]              = useState([]);
  const [bureaus,                  setBureaus]                  = useState([]);
  const [managements,              setManagements]              = useState([]);
  const [pickerOpen,               setPickerOpen]               = useState({});
  const [validationErrors,        setValidationErrors]        = useState({});
  const [showFieldErrors,         setShowFieldErrors]         = useState(false);
  const [isSingleDay,             setIsSingleDay]             = useState(false);

  const [formData, setFormData] = useState({
    name: '', type: '', start_date: null, end_date: null, start_time: null, end_time: null,
    venue: '', status: 'Pending', remark: '',
    design_start_date: null, design_end_date: null, mechanism_start_date: null, mechanism_end_date: null,
    electronic_start_date: null, electronic_end_date: null, programming_start_date: null, programming_end_date: null,
    tuning_start_date: null, tuning_end_date: null, demo_day: null, demo_time: null, teams: [],
    programming_representatives: [], electronic_representatives: [], mechanism_representatives: [],
    inventory_representatives: [], meeting_representatives: [], mop_floor_representatives: [],
    all_users: true,
    department_selection: [],
    bureau_selection: [],
    management_selection: [],
  });

  const { formDraft, submitDraftProps } = useFormDraftBindings({
    formKey: 'event',
    userId: currentUser?.id,
    isEditMode: isEdit,
    enabled: !loading,
    formData,
    setFormData,
    mergeSnapshot: (prev, data) => {
      if (data?.type) setEventType(data.type);
      if (isValidPickerDate(data?.start_date) && isValidPickerDate(data?.end_date)) {
        setIsSingleDay(dayjs(data.start_date).isSame(dayjs(data.end_date), 'day'));
      }
      return { ...prev, ...data };
    },
  });

  useEffect(() => {
    const cssId = 'eventform-ds-css';
    if (!document.getElementById(cssId)) {
      const el = document.createElement('style');
      el.id = cssId; el.innerHTML = FORM_CSS;
      document.head.appendChild(el);
    }
  }, []);

  // Prefill date/time when coming from the calendar create flow.
  useEffect(() => {
    if (!currentUser || isEdit) return;

    const startDateStr = searchParams.get('start_date');
    const endDateStr   = searchParams.get('end_date');
    const startTimeStr = searchParams.get('start_time');
    const endTimeStr   = searchParams.get('end_time');

    const hasAny =
      Boolean(startDateStr) || Boolean(endDateStr) ||
      Boolean(startTimeStr) || Boolean(endTimeStr);
    if (!hasAny) return;

    setFormData(prev => {
      const nextStart = prev.start_date || (startDateStr ? dayjs(startDateStr) : null);
      const nextEnd = prev.end_date || (endDateStr ? dayjs(endDateStr) : null);
      if (isValidPickerDate(nextStart) && isValidPickerDate(nextEnd)) {
        setIsSingleDay(dayjs(nextStart).isSame(dayjs(nextEnd), 'day'));
      }
      return {
        ...prev,
        start_date: nextStart,
        end_date: nextEnd,
        start_time: prev.start_time || toPickerTime(startTimeStr),
        end_time: prev.end_time || toPickerTime(endTimeStr),
      };
    });
  }, [searchParams, currentUser, isEdit]);

  useEffect(() => {
    if (!currentUser) { navigate('/login'); return; }
    fetchInitialData();
  }, [isEdit, id, currentUser]);

  const fetchInitialData = async () => {
    try {
      setLoading(Boolean(isEdit && id));
      const requests = [
        axiosInstance.get('/users?minimal=1&active_only=1'),
        axiosInstance.get('/departments'),
        axiosInstance.get('/bureaus'),
        axiosInstance.get('/managements'),
      ];
      if (isEdit && id) {
        requests.push(eventService.getById(Number(id)));
      }
      const [usersRes, deptRes, bureauRes, mgmtRes, eventRes] = await Promise.all(requests);

      const usersData = Array.isArray(usersRes.data) ? usersRes.data : usersRes.data?.data || [];
      const byDepartment = (user, deptId) => Number(user?.department_id ?? user?.department?.id) === deptId;
      setUsers({
        programming: usersData.filter((u) => byDepartment(u, 1)),
        electronic:  usersData.filter((u) => byDepartment(u, 2)),
        mechanism:   usersData.filter((u) => byDepartment(u, 3)),
        all: usersData,
      });

      const normalize = (res) => (Array.isArray(res?.data) ? res.data : res?.data?.data || []);
      setDepartments(normalize(deptRes));
      setBureaus(normalize(bureauRes));
      setManagements(normalize(mgmtRes));

      if (isEdit && id && eventRes) {
        hydrateFormFromEvent(eventRes);
      }
    } catch {
      setError('Failed to load event form data');
    } finally {
      setLoading(false);
    }
  };

  const hydrateFormFromEvent = (event) => {
      const parseJ = (v) => { if (typeof v === 'string') { try { return JSON.parse(v); } catch { return []; } } return Array.isArray(v) ? v : []; };
      const f = {
        ...event,
        start_date:  dayjs(event.start_date),
        end_date:    dayjs(event.end_date),
        start_time:  toPickerTime(event.start_time),
        end_time:    toPickerTime(event.end_time),
        remark: event.remark || '',
        programming_representatives: [], electronic_representatives: [],
        mechanism_representatives: [], inventory_representatives: [],
        meeting_representatives: [], mop_floor_representatives: [], teams: [],
        all_users: Boolean(event.all_users),
        department_selection: Boolean(event.all_users) ? [] : parseJ(event.department_selection),
        bureau_selection: Boolean(event.all_users) ? [] : parseJ(event.bureau_selection),
        management_selection: Boolean(event.all_users) ? [] : parseJ(event.management_selection),
      };
      if (['Demo', 'Prototype'].includes(event.type) && event.training_details) {
        const d = event.training_details;
        Object.assign(f, {
          design_start_date:       dayjs(d.design_start_date),
          design_end_date:         dayjs(d.design_end_date),
          mechanism_start_date:    dayjs(d.mechanism_start_date),
          mechanism_end_date:      dayjs(d.mechanism_end_date),
          electronic_start_date:   dayjs(d.electronic_start_date),
          electronic_end_date:     dayjs(d.electronic_end_date),
          programming_start_date:  dayjs(d.programming_start_date),
          programming_end_date:    dayjs(d.programming_end_date),
          tuning_start_date:       dayjs(d.tuning_start_date),
          tuning_end_date:         dayjs(d.tuning_end_date),
          demo_day:  dayjs(d.demo_day),
          demo_time: toPickerTime(d.demo_time),
          teams: parseJ(d.teams),
        });
      }
      if (event.type === 'LabCleaning' && event.lab_cleaning_details) {
        const d = event.lab_cleaning_details;
        Object.assign(f, {
          programming_representatives: parseJ(d.programming_representatives),
          electronic_representatives:  parseJ(d.electronic_representatives),
          mechanism_representatives:   parseJ(d.mechanism_representatives),
          inventory_representatives:   parseJ(d.inventory_representatives),
          meeting_representatives:     parseJ(d.meeting_representatives),
          mop_floor_representatives:   parseJ(d.mop_floor_representatives),
        });
      }
      if (event.type === 'Training' && event.training_details) f.teams = parseJ(event.training_details.teams);
      setIsSingleDay(dayjs(event.start_date).isSame(dayjs(event.end_date), 'day'));
      setFormData(f);
      setEventType(event.type);
  };

  const DT_FIELDS = new Set(['start_date','end_date','start_time','end_time','design_start_date','design_end_date','mechanism_start_date','mechanism_end_date','electronic_start_date','electronic_end_date','programming_start_date','programming_end_date','tuning_start_date','tuning_end_date','demo_day','demo_time']);
  const makeEmptyTeam = () => ({
    team_name: '',
    programming_members: [],
    electronic_members: [],
    mechanism_members: [],
    operator_1_id: '',
    operator_2_id: '',
    operator_3_id: '',
    pit_crew_1_id: '',
    pit_crew_2_id: '',
    pit_crew_3_id: '',
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const normalizedValue = value === null ? (DT_FIELDS.has(field) ? null : '') : value;
      return {
        ...prev,
        [field]: normalizedValue,
        ...(field === 'start_date' && isSingleDay ? { end_date: normalizedValue } : {}),
      };
    });
    if (error) setError(null);
    if (showFieldErrors) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        if (next[field]) next[field] = '';
        if (field === 'start_date' && next.end_date) next.end_date = '';
        if (field === 'start_time' && next.end_time) next.end_time = '';
        return next;
      });
    }
  };

  const handleSingleDayChange = (checked) => {
    setIsSingleDay(checked);
    if (checked) {
      setFormData(prev => ({ ...prev, end_date: prev.start_date }));
      setValidationErrors(prev => ({ ...prev, end_date: '' }));
    }
  };

  const runFormValidation = () => {
    const activeType = formData.type || eventType;
    const fieldErrors = collectEventFormFieldErrors(formData, activeType);
    const blocking = collectEventFormBlockingIssues(formData, activeType);
    setValidationErrors(fieldErrors);
    if (!blocking.length) {
      setShowFieldErrors(false);
      return { ok: true, message: null, fieldErrors };
    }
    setShowFieldErrors(true);
    const message = Object.keys(fieldErrors).length
      ? formatFieldErrorsMessage(fieldErrors)
      : getEventFormValidationMessage(formData, activeType);
    return { ok: false, message, fieldErrors };
  };

  const handleTypeChange = (value) => {
    setEventType(value);
    if (error) setError(null);
    if (showFieldErrors && validationErrors.type) {
      setValidationErrors(prev => ({ ...prev, type: '' }));
    }
    setFormData(prev => {
      const needsAutoTeam = ['Demo', 'Prototype', 'Training'].includes(value) && (!Array.isArray(prev.teams) || prev.teams.length === 0);
      return {
        ...prev,
        type: value,
        ...(needsAutoTeam ? { teams: [makeEmptyTeam()] } : {}),
        ...(value !== 'Other'
          ? {
              all_users: true,
              department_selection: [],
              bureau_selection: [],
              management_selection: [],
            }
          : {}),
      };
    });
  };

  const handleSelectionChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
      ...(value.length > 0 ? { all_users: false } : {}),
    }));
  };

  const toggleAudienceId = (field, id) => {
    setFormData((prev) => {
      const cur = prev[field] || [];
      const numId = Number(id);
      const next = cur.some((x) => Number(x) === numId)
        ? cur.filter((x) => Number(x) !== numId)
        : [...cur, id];
      return { ...prev, [field]: next, ...(next.length > 0 ? { all_users: false } : {}) };
    });
  };

  const mergeAudienceIds = (field, ids) => {
    setFormData((prev) => {
      const cur = new Set((prev[field] || []).map((x) => Number(x)));
      ids.forEach((id) => cur.add(Number(id)));
      const next = Array.from(cur);
      return { ...prev, [field]: next, all_users: false };
    });
  };

  const handleAllUsersChange = (checked) => {
    setFormData(prev => ({
      ...prev,
      all_users: checked,
      ...(checked
        ? {
            department_selection: [],
            bureau_selection: [],
            management_selection: [],
          }
        : {}),
    }));
  };

  const addTeam    = () => setFormData(prev => ({ ...prev, teams: [...prev.teams, makeEmptyTeam()] }));
  const removeTeam = (i) => { setTeamDeleteIndex(i); setTeamDeleteDialogOpen(true); };
  const confirmRemoveTeam = () => {
    if (teamDeleteIndex === null) return;
    const activeType = formData.type || eventType;
    if (['Demo', 'Prototype'].includes(activeType) && formData.teams.length <= 1) {
      setError('At least one team is required.');
      setTeamDeleteDialogOpen(false);
      setTeamDeleteIndex(null);
      return;
    }
    setFormData(prev => ({ ...prev, teams: prev.teams.filter((_, idx) => idx !== teamDeleteIndex) }));
    setTeamDeleteDialogOpen(false); setTeamDeleteIndex(null);
  };
  const updateTeam = (i, field, value) => {
    setFormData(prev => ({ ...prev, teams: prev.teams.map((t, idx) => idx === i ? { ...t, [field]: value } : t) }));
    if (error) setError(null);
    if (showFieldErrors) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        if (next[`team_${i}_${field}`]) next[`team_${i}_${field}`] = '';
        if (next.teams) next.teams = '';
        return next;
      });
    }
  };
  const handleRepChange = (dept, uid, checked) => {
    const f = `${dept}_representatives`;
    const current = formData[f] || [];
    if (!checked && current.length <= 1 && current.includes(uid)) {
      return;
    }
    setFormData(prev => ({ ...prev, [f]: checked ? [...prev[f], uid] : prev[f].filter(id => id !== uid) }));
    if (error) setError(null);
    if (showFieldErrors && validationErrors[f]) {
      setValidationErrors(prev => ({ ...prev, [f]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      const fmt  = (d) => !d ? null : dayjs.isDayjs(d) ? d.format('YYYY-MM-DD') : dayjs(d).format('YYYY-MM-DD');
      const fmtt = (t) => formatPickerTime(t);
      const submitEventType = formData.type || eventType;
      const eventData = {
        name: formData.name.trim(), type: submitEventType,
        start_date: fmt(formData.start_date), end_date: fmt(formData.end_date),
        start_time: fmtt(formData.start_time), end_time: fmtt(formData.end_time),
        venue: formData.venue.trim(), status: formData.status,
        remark: formData.remark?.trim() || '',
      };
      if (['Demo','Prototype'].includes(submitEventType)) Object.assign(eventData, {
        overall_start_date: fmt(formData.design_start_date), overall_end_date: fmt(formData.programming_end_date),
        design_start_date: fmt(formData.design_start_date), design_end_date: fmt(formData.design_end_date),
        mechanism_start_date: fmt(formData.mechanism_start_date), mechanism_end_date: fmt(formData.mechanism_end_date),
        electronic_start_date: fmt(formData.electronic_start_date), electronic_end_date: fmt(formData.electronic_end_date),
        programming_start_date: fmt(formData.programming_start_date), programming_end_date: fmt(formData.programming_end_date),
        tuning_start_date: fmt(formData.tuning_start_date), tuning_end_date: fmt(formData.tuning_end_date),
        demo_day: fmt(formData.demo_day), demo_time: fmtt(formData.demo_time),
        teams: formData.teams || [],
      });
      if (submitEventType === 'LabCleaning') Object.assign(eventData, {
        programming_representatives: formData.programming_representatives,
        electronic_representatives:  formData.electronic_representatives,
        mechanism_representatives:   formData.mechanism_representatives,
        inventory_representatives:   formData.inventory_representatives,
        meeting_representatives:     formData.meeting_representatives,
        mop_floor_representatives:   formData.mop_floor_representatives,
      });
      if (submitEventType === 'Training') Object.assign(eventData, { teams: formData.teams });
      const isOtherType = submitEventType === 'Other';
      Object.assign(eventData, isOtherType
        ? {
            all_users: Boolean(formData.all_users),
            department_selection: formData.department_selection || [],
            bureau_selection:     formData.bureau_selection     || [],
            management_selection: formData.management_selection || [],
          }
        : {
            all_users: true,
            department_selection: [],
            bureau_selection: [],
            management_selection: [],
          });
      Object.keys(eventData).forEach(k => { if (eventData[k] === undefined || eventData[k] === null) delete eventData[k]; });
      if (isEdit && id) {
        setError(null);
        setValidationErrors({});
        setShowFieldErrors(false);
        setConfirmUpdateError('');
        setPendingUpdateRequest(eventData);
        setConfirmUpdateDialogOpen(true);
        return;
      }
      const { ok, message } = runFormValidation();
      if (!ok) {
        setError(message);
        return;
      }
      setLoading(true);
      await eventService.create(eventData);
      if (!isEdit) formDraft.clearDraft();
      setSuccess('Event created successfully!');
      setTimeout(() => navigate('/events'), 600);
    } catch (err) {
      const d = err.response?.data;
      if (d?.errors) {
        applyApiValidationErrors(d, setValidationErrors);
        setShowFieldErrors(true);
        setError(formatFieldErrorsMessage(d.errors, err.message || 'Failed to save event.'));
      } else {
        setError(formatApiErrorMessage(d, err.message || 'Failed to save event.'));
      }
    } finally { setLoading(false); }
  };

  const confirmUpdate = async () => {
    if (!pendingUpdateRequest || !id) return;
    setConfirmUpdateError('');
    const { ok, message } = runFormValidation();
    if (!ok) {
      setConfirmUpdateError(message || 'Please fix the highlighted fields and try again.');
      return;
    }
    try {
      setLoading(true);
      await eventService.update(Number(id), pendingUpdateRequest);
      setSuccess('Event updated successfully!');
      setTimeout(() => navigate('/events'), 600);
      setConfirmUpdateDialogOpen(false);
      setPendingUpdateRequest(null);
      setConfirmUpdateError('');
    } catch (err) {
      const d = err.response?.data;
      if (d?.errors) {
        applyApiValidationErrors(d, setValidationErrors);
        setShowFieldErrors(true);
        setConfirmUpdateError(formatFieldErrorsMessage(d.errors, err.message || 'Failed to save event.'));
      } else {
        setConfirmUpdateError(formatApiErrorMessage(d, err.message || 'Failed to save event.'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (!currentUser) return <Container maxWidth="lg" sx={{ mt: 4 }}><FeedbackMessage message="Please log in." severity="error" /></Container>;
  if (loading && isEdit) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><Loading message="Loading event form" size={56} variant="primary" /></Box>;

  const activeEventType = formData.type || eventType;
  const isOtherEventType = activeEventType === 'Other';
  const canSubmit = collectEventFormRequiredFieldIssues(formData, activeEventType).length === 0;
  const submitDisabled = loading || !canSubmit;
  const { fieldHelper, fieldHasError } = createFieldErrorGate(showFieldErrors, validationErrors);
  const pickerFieldErrors = (name) => ({
    error: fieldHasError(name),
    helperText: fieldHelper(name),
  });

  const periodPairs = [
    { label: 'Design Period',      startF: 'design_start_date',      endF: 'design_end_date',      neon: NEON  },
    { label: 'Mechanism Period',   startF: 'mechanism_start_date',   endF: 'mechanism_end_date',   neon: NEON2 },
    { label: 'Electronic Period',  startF: 'electronic_start_date',  endF: 'electronic_end_date',  neon: '#FCD34D' },
    { label: 'Programming Period', startF: 'programming_start_date', endF: 'programming_end_date', neon: NEON3 },
    { label: 'Tuning Period',      startF: 'tuning_start_date',      endF: 'tuning_end_date',      neon: '#F87171' },
  ];

  const labDepts = [
    { key: 'programming', label: 'Programming Room', users: users.programming,                        neon: NEON,      icon: <ComputerIcon sx={{ fontSize: 14 }} /> },
    { key: 'electronic',  label: 'Electronic Room',  users: users.electronic,                         neon: NEON2,     icon: <EngineeringIcon sx={{ fontSize: 14 }} /> },
    { key: 'mechanism',   label: 'Mechanism Room',   users: users.mechanism,                          neon: NEON3,     icon: <SettingsIcon sx={{ fontSize: 14 }} /> },
    { key: 'inventory',   label: 'Inventory',        users: users.all.filter((u) => Number(u?.bureau_id ?? u?.bureau?.id) === 6), neon: '#FCD34D', icon: <PersonIcon sx={{ fontSize: 14 }} /> },
    { key: 'meeting',     label: 'Meeting',          users: users.all,                                neon: '#F87171', icon: <GroupIcon sx={{ fontSize: 14 }} /> },
    { key: 'mop_floor',   label: 'Mop Floor',        users: users.all,                                neon: NEON2,     icon: <PersonIcon sx={{ fontSize: 14 }} /> },
  ];

  // ── Picker slot configs (normal + small, date + time) ─────────────────────
  const datePickerSlots      = makePickerSlots('normal', <CalendarTodayIcon sx={{ color: alpha(NEON, 0.45) }} />);
  const datePickerSlotsSmall = makePickerSlots('small',  <CalendarTodayIcon sx={{ color: alpha(NEON, 0.45) }} />);
  const timePickerSlots      = makePickerSlots('normal', <ScheduleIcon sx={{ color: alpha(NEON, 0.45) }} />);
  const timePickerSlotsSmall = makePickerSlots('small',  <ScheduleIcon sx={{ color: alpha(NEON, 0.45) }} />);
  const bindPicker = (pickerKey, slotFactory, textFieldExtra = {}, disabled = false) =>
    buildPickerFieldProps(pickerKey, pickerOpen, setPickerOpen, slotFactory(disabled), { disabled, textField: textFieldExtra });

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="lg" sx={{ mt: 2, mb: 6 }}>

        <Header
          title={isEdit ? 'Edit Event' : 'Create New Event'}
          subtitle={isEdit ? 'Update event information and details' : 'Add a new event to the system'}
          icon={EventSidebarIcon}
          showBackButton
          onBackClick={() => navigate('/events')}
        />

        <FeedbackMessage message={error}   severity="error"   onClose={() => setError(null)}
          sx={{ fontFamily: "'DM Sans',sans-serif", fontSize: '0.9rem', background: alpha('#450a0a', 0.92), border: '1px solid rgba(248,113,113,0.3)', color: '#fca5a5', boxShadow: '0 4px 20px rgba(239,68,68,0.12)' }} />
        <FeedbackMessage message={success} severity="success" onClose={() => setSuccess(null)}
          sx={{ fontFamily: "'DM Sans',sans-serif", fontSize: '0.9rem', fontWeight: 500, background: `linear-gradient(135deg,${alpha(NEON3,0.1)},${alpha(SURFACE_MID,0.95)})`, border: `1px solid ${alpha(NEON3,0.35)}`, boxShadow: `0 4px 20px ${alpha(NEON3,0.15)}`, color: alpha(NEON3,0.95) }} />

        {formDraft.showNotice && (
          <FormDraftNotice
            savedAt={formDraft.restoredAt}
            onDiscard={formDraft.discardDraft}
            onDismiss={formDraft.dismissNotice}
          />
        )}

        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

            {/* ── Basic Information ──────────────────────────────────────────── */}
            <SectionCard title="Basic Information" icon={<EventIcon />} delay={0}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5 }}>
                  <StyledField
                    fullWidth required label="Event Name" name="name" value={formData.name}
                    onChange={e => handleInputChange('name', e.target.value)}
                    error={fieldHasError('name')}
                    helperText={fieldHelper('name')}
                    InputProps={{ startAdornment: <InputAdornment position="start"><NameIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} /></InputAdornment> }}
                  />
                  <SearchableSelect
                    required
                    label="Event Type"
                    value={formData.type}
                    onChange={handleTypeChange}
                    options={['Demo', 'Prototype', 'Training', 'LabCleaning', 'Other'].map((t) => ({ value: t, label: t }))}
                    startIcon={<EventIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} />}
                    error={fieldHasError('type')}
                    helperText={fieldHelper('type')}
                    sx={{ flex: 1 }}
                  />
                </Box>

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5 }}>
                  <DatePicker
                    label={isSingleDay ? 'Event Date' : 'Start Date'}
                    value={formData.start_date}
                    onChange={v => handleInputChange('start_date', v)}
                    {...bindPicker('start_date', datePickerSlots, { required: true, ...pickerFieldErrors('start_date') })}
                  />
                  {!isSingleDay && (
                    <DatePicker
                      label="End Date"
                      value={formData.end_date}
                      onChange={v => handleInputChange('end_date', v)}
                      minDate={formData.start_date || undefined}
                      {...bindPicker('end_date', datePickerSlots, { required: true, ...pickerFieldErrors('end_date') })}
                    />
                  )}
                </Box>

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5 }}>
                  <TimePicker
                    label="Start Time"
                    value={formData.start_time}
                    onChange={v => handleInputChange('start_time', v)}
                    {...bindPicker('start_time', timePickerSlots, pickerFieldErrors('start_time'))}
                  />
                  <TimePicker
                    label="End Time"
                    value={formData.end_time}
                    onChange={v => handleInputChange('end_time', v)}
                    {...bindPicker('end_time', timePickerSlots, pickerFieldErrors('end_time'))}
                  />
                </Box>

                
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isSingleDay}
                      onChange={(e) => handleSingleDayChange(e.target.checked)}
                      sx={{ color: alpha(NEON, 0.45), '&.Mui-checked': { color: NEON } }}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ color: TEXT_BODY, fontSize: '0.9rem', fontWeight: 600 }}>
                        One-day event
                      </Typography>
                      <Typography sx={{ color: alpha(TEXT_BODY, 0.5), fontSize: '0.75rem' }}>
                        Use the same date for the start and end
                      </Typography>
                    </Box>
                  }
                  sx={{ alignSelf: 'flex-start', m: 0 }}
                />

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5 }}>
                  <StyledField
                    fullWidth required label="Venue" name="venue" value={formData.venue}
                    onChange={e => handleInputChange('venue', e.target.value)}
                    error={fieldHasError('venue')}
                    helperText={fieldHelper('venue')}
                    InputProps={{ startAdornment: <InputAdornment position="start"><LocationIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} /></InputAdornment> }}
                  />
                  <SearchableSelect
                    required
                    label="Status"
                    value={formData.status}
                    onChange={(v) => handleInputChange('status', v)}
                    options={['Pending', 'In Progress', 'Completed', 'Cancelled'].map((s) => ({ value: s, label: s }))}
                    startIcon={<FlagIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} />}
                  />
                </Box>

                <StyledField
                  fullWidth label="Remark" name="remark" value={formData.remark}
                  onChange={e => handleInputChange('remark', e.target.value)}
                  multiline rows={3}
                  InputProps={{ startAdornment: <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}><NotesIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} /></InputAdornment> }}
                />
              </Box>
            </SectionCard>

            {/* ── Audience Target ────────────────────────────────────────────── */}
            {isOtherEventType && (
            <SectionCard title="Audience Target" icon={<GroupIcon />} delay={0.04} accentNeon={NEON}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Typography sx={{ fontSize: '0.85rem', color: alpha(TEXT_BODY, 0.65), lineHeight: 1.5, maxWidth: 720 }}>
                  Choose who should see this event. Select <strong style={{ color: NEON }}>All Team</strong> for everyone, or pick specific departments, bureaus, and management roles below.
                </Typography>

                <Box
                  onClick={() => handleAllUsersChange(!formData.all_users)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2,
                    borderRadius: '12px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    border: `1.5px solid ${formData.all_users ? alpha(NEON, 0.55) : alpha(NEON, 0.18)}`,
                    background: formData.all_users
                      ? `linear-gradient(135deg, ${alpha(NEON, 0.12)}, ${alpha(B[700], 0.08)})`
                      : alpha(FORM_SURFACE_UP, 0.45),
                    boxShadow: formData.all_users ? `0 0 20px ${alpha(NEON, 0.12)}` : 'none',
                    transition: 'all 0.22s ease',
                    '&:hover': { borderColor: alpha(NEON, 0.45) },
                  }}
                >
                  <Checkbox
                    checked={Boolean(formData.all_users)}
                    onChange={(e) => handleAllUsersChange(e.target.checked)}
                    onClick={(e) => e.stopPropagation()}
                    sx={{ color: alpha('#fff', 0.4), '&.Mui-checked': { color: NEON } }}
                  />
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: formData.all_users ? NEON : alpha(TEXT_BODY, 0.85) }}>
                      Entire team
                    </Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: alpha(TEXT_BODY, 0.5), mt: 0.25 }}>
                      No department / bureau / role filters needed
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                    gap: 2,
                  }}
                >
                  <AudiencePickerColumn
                    title="Departments"
                    icon={<EngineeringIcon sx={{ fontSize: 18, color: NEON }} />}
                    accentColor={NEON}
                    items={departments}
                    selectedIds={formData.department_selection}
                    onToggle={(id) => toggleAudienceId('department_selection', id)}
                    onSelectAll={(ids) => mergeAudienceIds('department_selection', ids)}
                    onClear={() => handleSelectionChange('department_selection', [])}
                    disabled={formData.all_users}
                  />
                  <AudiencePickerColumn
                    title="Bureaus"
                    icon={<PersonIcon sx={{ fontSize: 18, color: NEON2 }} />}
                    accentColor={NEON2}
                    items={bureaus}
                    selectedIds={formData.bureau_selection}
                    onToggle={(id) => toggleAudienceId('bureau_selection', id)}
                    onSelectAll={(ids) => mergeAudienceIds('bureau_selection', ids)}
                    onClear={() => handleSelectionChange('bureau_selection', [])}
                    disabled={formData.all_users}
                  />
                  <AudiencePickerColumn
                    title="Managements"
                    icon={<GroupIcon sx={{ fontSize: 18, color: NEON3 }} />}
                    accentColor={NEON3}
                    items={managements}
                    selectedIds={formData.management_selection}
                    onToggle={(id) => toggleAudienceId('management_selection', id)}
                    onSelectAll={(ids) => mergeAudienceIds('management_selection', ids)}
                    onClear={() => handleSelectionChange('management_selection', [])}
                    disabled={formData.all_users}
                  />
                </Box>

                {!formData.all_users && (() => {
                  const allSelected = [
                    ...(formData.department_selection || []).map((id) => ({ label: departments.find((d) => Number(d.id) === Number(id))?.name, color: NEON })),
                    ...(formData.bureau_selection || []).map((id) => ({ label: bureaus.find((b) => Number(b.id) === Number(id))?.name, color: NEON2 })),
                    ...(formData.management_selection || []).map((id) => ({ label: managements.find((m) => Number(m.id) === Number(id))?.name, color: NEON3 })),
                  ].filter((x) => x.label);
                  if (!allSelected.length) {
                    return (
                      <Typography sx={{ fontSize: '0.8rem', color: alpha(TEXT_BODY, 0.45), fontStyle: 'italic' }}>
                        No audience filters selected — pick at least one group or enable Entire team.
                      </Typography>
                    );
                  }
                  return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                      {allSelected.map(({ label, color }, i) => (
                        <Chip
                          key={`${label}-${i}`}
                          label={label}
                          size="small"
                          sx={{
                            height: 24,
                            fontSize: '0.75rem',
                            color,
                            background: alpha(color, 0.1),
                            border: `1px solid ${alpha(color, 0.3)}`,
                          }}
                        />
                      ))}
                    </Box>
                  );
                })()}
              </Box>
            </SectionCard>
            )}

            {/* ── Demo / Prototype Timeline & Teams ─────────────────────────── */}
            {['Demo','Prototype'].includes(activeEventType) && (
              <SectionCard title={`${activeEventType} Timeline & Teams`} icon={<ScheduleIcon />} delay={0.06} accentNeon={NEON2}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
                  {periodPairs.map(({ label, startF, endF, neon }) => (
                    <PeriodBox key={label} label={label} neon={neon}>
                      <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <DatePicker
                          label={<span>Start <span style={{ color: 'red' }}>*</span></span>}
                          value={formData[startF]}
                          onChange={v => handleInputChange(startF, v)}
                          {...bindPicker(startF, datePickerSlotsSmall, pickerFieldErrors(startF))}
                        />
                        <DatePicker
                          label={<span>End <span style={{ color: 'red' }}>*</span></span>}
                          value={formData[endF]}
                          onChange={v => handleInputChange(endF, v)}
                          {...bindPicker(endF, datePickerSlotsSmall, pickerFieldErrors(endF))}
                        />
                      </Box>
                    </PeriodBox>
                  ))}
                  <PeriodBox label="Demo Day" neon="#F87171">
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <DatePicker
                        label={<span>Date <span style={{ color: 'red' }}>*</span></span>}
                        value={formData.demo_day}
                        onChange={v => handleInputChange('demo_day', v)}
                        {...bindPicker('demo_day', datePickerSlotsSmall, pickerFieldErrors('demo_day'))}
                      />
                      <TimePicker
                        label={<span>Time <span style={{ color: 'red' }}>*</span></span>}
                        value={formData.demo_time}
                        onChange={v => handleInputChange('demo_time', v)}
                        {...bindPicker('demo_time', timePickerSlotsSmall, pickerFieldErrors('demo_time'))}
                      />
                    </Box>
                  </PeriodBox>
                </Box>

                {/* Teams */}
                <Box sx={{ borderTop: `1px solid ${alpha(NEON, 0.12)}`, pt: 2.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography sx={{ fontFamily: "'Rajdhani',sans-serif", fontWeight: 700, fontSize: '0.95rem', color: alpha(TEXT_BODY, 0.9) }}>Teams</Typography>
                    <Button onClick={addTeam} startIcon={<AddIcon />} sx={{ borderRadius: '8px', px: 2, fontFamily: "'DM Sans',sans-serif", fontWeight: 700, fontSize: '0.8rem', border: `1px solid ${alpha(NEON, 0.35)}`, color: NEON, background: alpha(NEON, 0.07), '&:hover': { background: alpha(NEON, 0.14), borderColor: alpha(NEON, 0.6) } }}>Add Team</Button>
                  </Box>
                  {showFieldErrors && validationErrors.teams && (
                    <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.68rem', color: '#F87171', mb: 1.5 }}>
                      {validationErrors.teams}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {formData.teams.map((team, idx) => (
                      <Box key={idx} sx={{ p: 2.5, borderRadius: '12px', border: `1px solid ${alpha(NEON, 0.15)}`, background: alpha(SURFACE_UP, 0.5) }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography sx={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '0.7rem', fontWeight: 600, letterSpacing: 1.2, textTransform: 'uppercase', color: alpha(NEON, 0.65) }}>Team {idx + 1}</Typography>
                          <IconButton
                            onClick={() => removeTeam(idx)}
                            disabled={formData.teams.length <= 1}
                            size="small"
                            sx={{ color: '#F87171', bgcolor: alpha('#F87171', 0.08), borderRadius: '8px', border: `1px solid ${alpha('#F87171', 0.2)}`, '&:hover': { bgcolor: alpha('#F87171', 0.18) }, '&.Mui-disabled': { opacity: 0.35 } }}
                          >
                            <DeleteIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Box>
                        <StyledField fullWidth label="Team Name" value={team.team_name} onChange={e => updateTeam(idx, 'team_name', e.target.value)} required size="small" sx={{ mb: 2 }} />
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                          {[
                            ['Programming', 'programming_members', users.programming, NEON,  ComputerIcon],
                            ['Electronic',  'electronic_members',  users.electronic,  NEON2, EngineeringIcon],
                            ['Mechanism',   'mechanism_members',   users.mechanism,   NEON3, SettingsIcon],
                          ].map(([label, field, list, n, Icon]) => {
                            const hasMembers = team[field]?.length > 0;
                            const deptErrorKey = teamDeptFieldKey(idx, field);
                            const deptHasError = showFieldErrors && validationErrors[deptErrorKey];
                            return (
                              <Box key={label} sx={{ flex: '1 1 150px', p: 1.5, borderRadius: '10px', background: alpha(n, 0.05), border: `1px solid ${deptHasError ? alpha('#F87171', 0.75) : hasMembers ? alpha(n, 0.45) : alpha(n, 0.18)}`, transition: 'border-color 0.2s ease' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <Icon sx={{ fontSize: 13, color: n }} />
                                  <Typography sx={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '0.58rem', fontWeight: 600, letterSpacing: 1.2, textTransform: 'uppercase', color: alpha(n, 0.75) }}>
                                    {label}{' '}
                                    <Typography component="span" sx={{ color: '#F87171', fontWeight: 700, textTransform: 'uppercase' }}>min 1</Typography>
                                  </Typography>
                                </Box>
                                <FormGroup>
                                  {list.map(u => (
                                    <FormControlLabel key={u.id}
                                      control={
                                        <Checkbox
                                          checked={team[field]?.includes(u.id) || false}
                                          onChange={e => {
                                            const cur = team[field] || [];
                                            if (!e.target.checked && cur.includes(u.id) && cur.length <= 1) return;
                                            updateTeam(idx, field, e.target.checked ? [...cur, u.id] : cur.filter(id => id !== u.id));
                                          }}
                                          sx={{ color: alpha(n, 0.4), '&.Mui-checked': { color: n }, '& .MuiSvgIcon-root': { fontSize: 16 } }}
                                        />
                                      }
                                      label={<Typography sx={{ fontFamily: "'DM Sans',sans-serif", fontSize: '0.82rem', color: alpha(TEXT_BODY, 0.85) }}>{u.name}</Typography>}
                                    />
                                  ))}
                                </FormGroup>
                                {deptHasError && (
                                  <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.62rem', color: '#F87171', mt: 1 }}>
                                    {validationErrors[deptErrorKey]}
                                  </Typography>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </SectionCard>
            )}

            {/* ── Training Teams ─────────────────────────────────────────────── */}
            {activeEventType === 'Training' && (
              <SectionCard title="Training Teams" icon={<GroupIcon />} delay={0.06} accentNeon={NEON3}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography sx={{ fontFamily: "'Rajdhani',sans-serif", fontWeight: 700, fontSize: '0.95rem', color: alpha(TEXT_BODY, 0.9) }}>Teams</Typography>
                  <Button onClick={addTeam} startIcon={<AddIcon />} sx={{ borderRadius: '8px', px: 2, fontFamily: "'DM Sans',sans-serif", fontWeight: 700, fontSize: '0.8rem', border: `1px solid ${alpha(NEON3, 0.35)}`, color: NEON3, background: alpha(NEON3, 0.07), '&:hover': { background: alpha(NEON3, 0.14), borderColor: alpha(NEON3, 0.6) } }}>Add Team</Button>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {formData.teams.map((team, idx) => (
                    <Box key={idx} sx={{ p: 2.5, borderRadius: '12px', border: `1px solid ${alpha(NEON3, 0.18)}`, background: alpha(SURFACE_UP, 0.5) }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography sx={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '0.7rem', fontWeight: 600, letterSpacing: 1.2, textTransform: 'uppercase', color: alpha(NEON3, 0.65) }}>Team {idx + 1}</Typography>
                        <IconButton onClick={() => removeTeam(idx)} size="small" sx={{ color: '#F87171', bgcolor: alpha('#F87171', 0.08), borderRadius: '8px', border: `1px solid ${alpha('#F87171', 0.2)}`, '&:hover': { bgcolor: alpha('#F87171', 0.18) } }}><DeleteIcon sx={{ fontSize: 15 }} /></IconButton>
                      </Box>
                      <StyledField fullWidth label="Team Name" value={team.team_name} onChange={e => updateTeam(idx, 'team_name', e.target.value)} required size="small" sx={{ mb: 2 }} />
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        {TRAINING_ROLE_GROUPS.map(({ groupLabel, accent, fields }) => (
                          <Box
                            key={groupLabel}
                            sx={{
                              flex: '1 1 280px',
                              p: 2,
                              borderRadius: '12px',
                              background: alpha(accent, 0.05),
                              border: `1px solid ${alpha(accent, 0.22)}`,
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: "'JetBrains Mono',monospace",
                                fontSize: '0.62rem',
                                fontWeight: 600,
                                letterSpacing: 1.2,
                                textTransform: 'uppercase',
                                color: alpha(accent, 0.8),
                                mb: 2,
                              }}
                            >
                              {groupLabel}
                            </Typography>
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                flexWrap: 'wrap',
                                gap: 2,
                              }}
                            >
                              {fields.map(({ field, label }) => (
                                <SearchableSelect
                                  key={field}
                                  required
                                  label={label}
                                  value={team[field] ?? ''}
                                  onChange={(v) => updateTeam(idx, field, v)}
                                  options={buildTrainingMemberOptions(team, field, users.all)}
                                  startIcon={<PersonIcon sx={{ color: alpha(accent, 0.45), fontSize: 18 }} />}
                                  sx={{ flex: '1 1 180px', minWidth: 0 }}
                                />
                              ))}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </SectionCard>
            )}

            {/* ── Lab Cleaning Representatives ───────────────────────────────── */}
            {activeEventType === 'LabCleaning' && (
              <SectionCard title="Lab Cleaning Representatives" icon={<SettingsIcon />} delay={0.06} accentNeon="#FCD34D">
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {labDepts.map(dept => {
                    const repField = `${dept.key}_representatives`;
                    const repCount = formData[repField]?.length || 0;
                    const repHasError = showFieldErrors && validationErrors[repField];
                    return (
                    <Box
                      key={dept.key}
                      sx={{
                        flex: '1 1 calc(50% - 8px)',
                        minWidth: '200px',
                        p: 2,
                        borderRadius: '12px',
                        background: alpha(dept.neon, 0.05),
                        border: `1px solid ${
                          repHasError
                            ? alpha('#F87171', 0.75)
                            : repCount > 0
                            ? alpha(dept.neon, 0.45)
                            : alpha(dept.neon, 0.2)
                        }`,
                        transition: 'border-color 0.2s ease',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <Box sx={{ color: dept.neon }}>{dept.icon}</Box>
                        <Typography sx={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '0.62rem', fontWeight: 600, letterSpacing: 1.2, textTransform: 'uppercase', color: alpha(dept.neon, 0.75) }}>
                          {dept.label}{' '}
                          <Typography component="span" sx={{ color: '#F87171', fontWeight: 300, textTransform: 'uppercase' }}>min 1</Typography>
                        </Typography>
                      </Box>
                      <Box sx={{ maxHeight: 180, overflowY: 'auto' }}>
                        <FormGroup>
                          {dept.users.map(u => (
                            <FormControlLabel key={u.id}
                              control={
                                <Checkbox
                                  checked={formData[repField]?.includes(u.id) || false}
                                  onChange={e => handleRepChange(dept.key, u.id, e.target.checked)}
                                  sx={{ color: alpha(dept.neon, 0.4), '&.Mui-checked': { color: dept.neon }, '& .MuiSvgIcon-root': { fontSize: 16 } }}
                                />
                              }
                              label={<Typography sx={{ fontFamily: "'DM Sans',sans-serif", fontSize: '0.82rem', color: alpha(TEXT_BODY, 0.85) }}>{u.name}</Typography>}
                            />
                          ))}
                        </FormGroup>
                      </Box>
                      {repHasError && (
                        <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.62rem', color: '#F87171', mt: 1 }}>
                          {validationErrors[repField]}
                        </Typography>
                      )}
                    </Box>
                    );
                  })}
                </Box>
              </SectionCard>
            )}

            {/* ── Submit ─────────────────────────────────────────────────────── */}
            <SubmitActionsCard
              statusText={isEdit ? 'Save changes to update this event.' : 'Complete the form to add a new event.'}
              missingRequiredFields={isEdit ? null : collectEventFormRequiredFieldIssues(formData, activeEventType)}
              readyStatusText="All required fields are complete. Ready to create event."
              cancelLabel="Cancel"
              onCancel={() => navigate('/events')}
              cancelDisabled={loading}
              submitLabel={isEdit ? 'Update Event' : 'Create Event'}
              submitDisabled={submitDisabled}
              loading={loading}
              submitStartIcon={<SaveIcon />}
              showSubmit
              submitType="submit"
              {...submitDraftProps}
            />

          </Box>
        </form>

        <ConfirmDeleteDialog
          open={teamDeleteDialogOpen}
          onClose={() => { setTeamDeleteDialogOpen(false); setTeamDeleteIndex(null); }}
          onConfirm={confirmRemoveTeam}
          itemType="team"
          itemName={teamDeleteIndex !== null ? `Team ${teamDeleteIndex + 1}` : ''}
          description="This will remove the team from the event timeline."
          confirmLabel="Remove Team"
        />

        <ConfirmDeleteDialog
          open={confirmUpdateDialogOpen}
          onClose={() => {
            if (loading) return;
            setConfirmUpdateDialogOpen(false);
            setPendingUpdateRequest(null);
            setConfirmUpdateError('');
          }}
          onConfirm={confirmUpdate}
          variant="edit"
          title="Confirm Update"
          itemType="event"
          itemName={formData.name}
          description="This action will update the event details and cannot be undone."
          confirmLabel="Update Event"
          loading={loading}
          confirmDisabled={!canSubmit}
          errorMessage={confirmUpdateError}
        />

      </Container>
    </LocalizationProvider>
  );
};

export default EventForm;
